import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Switch,
  Box,
  HStack,
  Text,
  useColorModeValue,
  Spacer,
  Button,
  Stack,
  Divider,
  useToast,
  Link,
  Heading,
  Menu,
  MenuButton,
  MenuList,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
  finleyAssertUnreachable,
} from '@finley-technologies/common';

import {
  DeliverableContainerReceiptActionsGQLType,
  DeliverablesOrgLevelCommentsViewGQLType,
  ThreadGQLType,
  useCreateNewThreadWithCommentForDeliverableContainerMutation,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { CommentThread } from './Thread';
import {
  getDeliverableDetailViewUrl,
  getDeliverableFinalOutputReceiptsViewUrl,
} from './utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { MultiSelect } from 'packages/common-frontend/components/MultiSelect';
import { RichTextEditor } from 'packages/common-frontend/components/RichTextEditor/RichTextEditor';
import {
  Select,
  formatSelectOptions,
} from 'packages/common-frontend/components/Select';
import { errorToastConfig } from 'packages/common-frontend/constants';

const audienceFilterOptions = ['all', 'internal', 'external'] as const;
type AudienceFilterType = typeof audienceFilterOptions[number];

type FilterType = {
  audienceFilter: AudienceFilterType;
  showParticipantFilter: boolean;
  titleText: string;
  externalParticipantIds: string[];
};

const FilterButton = ({
  externalOrgOptions,
  onApply,
  currentFilter,
}: {
  externalOrgOptions: { value: string; label: string }[];
  onApply: (newCurrentFilter: FilterType) => void;
  currentFilter: FilterType | undefined;
}) => {
  // audience filter
  const [audienceFilter, setAudienceFilter] = useState<AudienceFilterType>(
    currentFilter ? currentFilter.audienceFilter : 'all',
  );

  // participant filter
  const [showParticipantFilter, setShowParticipantFilter] = useState<boolean>(
    currentFilter ? currentFilter.showParticipantFilter : true,
  );
  const [titleText, setTitleText] = useState<string>(
    currentFilter ? currentFilter.titleText : 'All Participants',
  );
  const allExternalParticipantIds = externalOrgOptions.map(
    (option) => option.value,
  );
  const [selectedParticipantIds, setSelectedExternalPartyIds] = useState<
    string[]
  >(
    currentFilter
      ? currentFilter.externalParticipantIds
      : allExternalParticipantIds,
  );

  const onChange = (newValue: string) => {
    const copyOfSelectedParticipantIds = _.clone(selectedParticipantIds);
    const index = copyOfSelectedParticipantIds.indexOf(newValue);
    if (index >= 0) {
      // only remove option if multiple are selected
      if (copyOfSelectedParticipantIds.length > 1) {
        copyOfSelectedParticipantIds.splice(index, 1);
      }
    } else {
      copyOfSelectedParticipantIds.push(newValue);
    }
    setSelectedExternalPartyIds(copyOfSelectedParticipantIds);

    if (copyOfSelectedParticipantIds.length > 0) {
      if (copyOfSelectedParticipantIds.length === externalOrgOptions.length) {
        setTitleText('All Participants');
      } else {
        const selectedOptions = externalOrgOptions.filter((option) =>
          copyOfSelectedParticipantIds.includes(option.value),
        );
        const labels = selectedOptions.map((option) =>
          _.startCase(option.label),
        );
        setTitleText(labels.join(', '));
      }
    } else {
      setTitleText('Select Participants');
    }
  };

  // use effect on select change
  useEffect(() => {
    if (audienceFilter === 'internal') {
      setShowParticipantFilter(false);
      setSelectedExternalPartyIds([]);
    } else {
      setShowParticipantFilter(true);
      setSelectedExternalPartyIds((curr) => {
        if (curr.length === 0) {
          return allExternalParticipantIds;
        }
        return curr;
      });
    }
  }, [allExternalParticipantIds, audienceFilter]);

  const onSubmit = useCallback(() => {
    switch (audienceFilter) {
      case 'all':
        return onApply({
          audienceFilter,
          showParticipantFilter,
          titleText,
          externalParticipantIds: selectedParticipantIds,
        });
      case 'external':
        return onApply({
          audienceFilter,
          showParticipantFilter,
          titleText,
          externalParticipantIds: selectedParticipantIds,
        });
      case 'internal':
        return onApply({
          audienceFilter,
          showParticipantFilter: false,
          titleText,
          externalParticipantIds: [],
        });
      default:
        return finleyAssertUnreachable(audienceFilter);
    }
  }, [
    audienceFilter,
    onApply,
    selectedParticipantIds,
    showParticipantFilter,
    titleText,
  ]);

  const onClear = useCallback(() => {
    setAudienceFilter(currentFilter ? currentFilter.audienceFilter : 'all');
    setShowParticipantFilter(
      currentFilter ? currentFilter.showParticipantFilter : true,
    );
    setTitleText(currentFilter ? currentFilter.titleText : 'All Participants');
    setSelectedExternalPartyIds(
      currentFilter
        ? currentFilter.externalParticipantIds
        : allExternalParticipantIds,
    );
  }, [allExternalParticipantIds, currentFilter]);

  return (
    <Menu autoSelect={false} eventListeners={{ scroll: true, resize: true }}>
      {({ onClose }) => (
        <>
          <MenuButton
            as={Button}
            leftIcon={<Icon icon="filter" size="small" />}
            size="sm"
            variant="link"
            textDecoration="none"
          >
            Filter
          </MenuButton>
          <MenuList
            p="16px"
            minW="300"
            maxW="800"
            left={0}
            color={colors.aurora[400]}
            backgroundColor={colors.millet[50]}
            fontSize="16px"
          >
            <Heading
              fontSize="sm"
              fontWeight="medium"
              mb="16px"
              textTransform="none"
            >
              Filter
            </Heading>
            <Stack fontSize="sm">
              <Text fontSize="sm">Audience</Text>
              <Select
                value={audienceFilter}
                onChange={(newVal) => {
                  if (newVal) {
                    setAudienceFilter(newVal);
                  }
                }}
                options={formatSelectOptions(audienceFilterOptions, 'title')}
                buttonProps={{ fontSize: 'small', size: 'md' }}
              />
              {showParticipantFilter && (
                <>
                  <Text fontSize="sm">Participants</Text>
                  <MultiSelect
                    options={externalOrgOptions}
                    onChange={(value: string) => {
                      onChange(value);
                    }}
                    onMenuClosed={() => {}}
                    selectedOptions={selectedParticipantIds}
                    titleText={titleText}
                    fullWidth
                    fontSize="small"
                  />
                </>
              )}
            </Stack>
            <Divider width="90%" margin="16px auto" />
            <HStack width="full" pl={1} pr={1}>
              <Button
                size="xs"
                py={4}
                px={12}
                justifyContent="flex-start"
                variant="outline"
                borderColor={colors.aurora[400]}
                borderWidth="2px"
                color={colors.aurora[400]}
                _focus={{ boxShadow: 'none' }}
                onClick={() => {
                  onClear();
                  onClose();
                }}
              >
                Clear
              </Button>
              <Button
                size="xs"
                py={4}
                px={12}
                ml={4}
                borderColor={colors.aurora[400]}
                borderWidth="2px"
                justifyContent="flex-start"
                variant="solid"
                _focus={{ boxShadow: 'none' }}
                colorScheme="aurora"
                color="white"
                onClick={() => {
                  onSubmit();
                  onClose();
                }}
              >
                Apply
              </Button>
            </HStack>
          </MenuList>
        </>
      )}
    </Menu>
  );
};

export const ContainerUpdatesDrawer = ({
  isOpen,
  onClose,
  deliverableContainerThreads,
  refetchThreads,
  receiptsActionLog,
  organizationId,
  userId,
  containerId,
  externalOrgOptions,
  reportName,
  creditFacilitySlug,
}: {
  isOpen: boolean;
  onClose: () => void;
  deliverableContainerThreads: ThreadGQLType[];
  refetchThreads: () => void;
  receiptsActionLog: (DeliverableContainerReceiptActionsGQLType & {
    version: number;
  })[];
  organizationId: string;
  userId: string;
  containerId: string;
  externalOrgOptions: { value: string; label: string }[];
  reportName: string;
  creditFacilitySlug: string;
}) => {
  const toast = useToast();
  const [newComment, setNewComment] = useState<string>('');
  const switchColor = useColorModeValue('gray.600', 'gray.200');
  const actionTextColor = useColorModeValue('gray.700', 'gray.200');

  const [isExternal, setIsExternal] = useState<boolean>(false);
  const [titleText, setTitleText] = useState<string>('Select External Parties');
  const [selectedExternalPartyIds, setSelectedExternalPartyIds] = useState<
    string[]
  >([]);
  const onChange = (newValue: string) => {
    const copyOfSelectedExternalPartyIds = _.clone(selectedExternalPartyIds);
    const index = copyOfSelectedExternalPartyIds.indexOf(newValue);
    if (index >= 0) {
      // only remove option if multiple are selected
      if (copyOfSelectedExternalPartyIds.length > 1) {
        copyOfSelectedExternalPartyIds.splice(index, 1);
      }
    } else {
      copyOfSelectedExternalPartyIds.push(newValue);
    }
    setSelectedExternalPartyIds(copyOfSelectedExternalPartyIds);

    if (copyOfSelectedExternalPartyIds.length > 0) {
      const selectedOptions = externalOrgOptions.filter((option) =>
        copyOfSelectedExternalPartyIds.includes(option.value),
      );
      const labels = selectedOptions.map((option) => _.startCase(option.label));
      setTitleText(labels.join(', '));
    } else {
      setTitleText('Select External Parties');
    }
  };

  const [currentFilter, setCurrentFilter] = useState<FilterType>();

  const allEvents = useMemo(() => {
    if (currentFilter) {
      const {
        audienceFilter: audienceType,
        externalParticipantIds,
      } = currentFilter;
      const filteredThreads = deliverableContainerThreads.filter((t) => {
        const orgIdsForThread = t.threadPermissions.map(
          (tp) => tp.organizationId,
        );

        const threadIsInternal =
          orgIdsForThread.length === 1 && orgIdsForThread[0] === organizationId;
        const threadIsExternal =
          _.difference(externalParticipantIds.sort(), orgIdsForThread.sort())
            .length === 0;

        switch (audienceType) {
          case 'internal':
            return threadIsInternal;
          case 'external':
            return threadIsExternal;
          case 'all':
            return threadIsInternal || threadIsExternal;
          default:
            return finleyAssertUnreachable(audienceType);
        }
      });

      return [...filteredThreads, ...receiptsActionLog];
    }
    return [...deliverableContainerThreads, ...receiptsActionLog];
  }, [
    deliverableContainerThreads,
    currentFilter,
    organizationId,
    receiptsActionLog,
  ]);

  allEvents.sort((a, b) => b.created_at - a.created_at);

  const eventsGroupedByMonth = _.groupBy(allEvents, (e) => {
    const fullDateStr = commonDate.epochToFullDateStr(e.created_at);
    const datetime = commonDate.fullDateStrToDateTime(fullDateStr);

    return `${datetime.monthLong} ${datetime.year}`;
  });

  const [
    createNewThreadWithCommentForDeliverableContainerMutation,
  ] = useCreateNewThreadWithCommentForDeliverableContainerMutation({
    fetchPolicy: 'network-only',
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent pt={2}>
        <DrawerCloseButton />
        <DrawerHeader>Updates</DrawerHeader>

        <DrawerBody>
          <Stack spacing="20px" alignItems="flex-start">
            <Box width="full">
              <RichTextEditor
                text={newComment}
                setText={setNewComment}
                isEditable
                placeHolderText="Leave a comment"
              />
            </Box>
            <HStack spacing="15px" color={switchColor} width="full" mb={4}>
              <Switch
                size="sm"
                isChecked={isExternal}
                onChange={() => setIsExternal(!isExternal)}
                _focus={{ boxShadow: 'none' }}
              />
              <Text fontSize="small">Make External</Text>

              <Spacer />
              {isExternal && (
                <Box width="45%">
                  <MultiSelect
                    options={externalOrgOptions}
                    onChange={(value: string) => {
                      onChange(value);
                    }}
                    optionsHeader="Select External Parties"
                    onMenuClosed={() => {}}
                    selectedOptions={selectedExternalPartyIds}
                    titleText={titleText}
                    fullWidth
                    fontSize="small"
                  />
                </Box>
              )}

              <Spacer />
              <Button
                py={4}
                px={5}
                justifyContent="flex-start"
                variant="solid"
                _focus={{ boxShadow: 'none' }}
                size="sm"
                colorScheme="aurora"
                color="white"
                isDisabled={isExternal && selectedExternalPartyIds.length === 0}
                onClick={async () => {
                  const organizationIds = [
                    ...new Set([organizationId, ...selectedExternalPartyIds]),
                  ];
                  await createNewThreadWithCommentForDeliverableContainerMutation(
                    {
                      variables: {
                        organizationIds,
                        comment: newComment,
                        authorUserId: userId,
                        deliverableContainerId: containerId,
                      },
                    },
                  );
                  refetchThreads();
                  setNewComment('');
                }}
              >
                Send Comment
              </Button>
            </HStack>

            <FilterButton
              externalOrgOptions={externalOrgOptions}
              onApply={(newCurrentFilter: FilterType) => {
                setCurrentFilter(newCurrentFilter);
              }}
              currentFilter={currentFilter}
            />
            <Divider />
            {Object.entries(eventsGroupedByMonth).map(([key, values]) => {
              return (
                <Stack key={key + JSON.stringify(values)} width="full">
                  <Text
                    fontSize="sm"
                    width="full"
                    textAlign="center"
                    fontWeight="bold"
                    mb={2}
                  >
                    {key}
                  </Text>
                  {values.map((val) => {
                    return (
                      <>
                        {val.__typename ===
                          'DeliverableContainerReceiptActions' && (
                          <Stack
                            fontSize="small"
                            mb={4}
                            color={actionTextColor}
                          >
                            <Text>
                              {commonDate.epochToLocaleDateString(
                                val.created_at,
                              )}
                            </Text>
                            <Text>
                              {`${_.startCase(val.userFirstName)} ${_.startCase(
                                val.userLastName,
                              )}, ${_.startCase(val.organizationName)}, ${
                                val.action === 'requestRemediation'
                                  ? 'requested remedition for'
                                  : val.action.toLowerCase()
                              }`}{' '}
                              <Link
                                href={getDeliverableFinalOutputReceiptsViewUrl({
                                  containerId,
                                  receiptId: val.receiptId,
                                })}
                                fontWeight="bold"
                                color="blue.300"
                              >{`Version ${val.version} of ${reportName}`}</Link>{' '}
                              {`for ${_.startCase(creditFacilitySlug)}.`}
                            </Text>
                          </Stack>
                        )}
                        {val.__typename === 'Thread' && (
                          <Stack mb={4}>
                            <CommentThread
                              thread={val}
                              organizationId={organizationId}
                              userId={userId}
                              refetchData={refetchThreads}
                            />
                          </Stack>
                        )}
                      </>
                    );
                  })}
                </Stack>
              );
            })}
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export const DeliverablesOverviewUpdatesDrawer = ({
  type,
  isOpen,
  onClose,
  threadsWithDeliverablesMetadata,
  refetchThreads,
  organizationId,
  userId,
}: {
  type: 'borrower' | 'lender';
  isOpen: boolean;
  onClose: () => void;
  threadsWithDeliverablesMetadata: DeliverablesOrgLevelCommentsViewGQLType[];
  refetchThreads: () => void;
  organizationId: string;
  userId: string;
}) => {
  const sortedThreads = threadsWithDeliverablesMetadata.sort(
    (a, b) => b.thread.created_at - a.thread.created_at,
  );
  const creditFacilityTextColor = useColorModeValue('gray.700', 'gray.200');

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent pt={2}>
        <DrawerCloseButton />
        <DrawerHeader>Deliverables Updates</DrawerHeader>

        <DrawerBody>
          <Stack spacing="20px" alignItems="flex-start">
            {sortedThreads.map((threadWithMetadata) => {
              const {
                thread,
                creditFacilitySlug,
                containerName,
                containerDueDate,
                containerId,
                receiptId,
              } = threadWithMetadata;

              const externalOrganizations = thread.threadPermissions.filter(
                (mapping) => mapping.organizationId !== organizationId,
              );

              const name = `${commonUtils.toTitleCase(
                containerName,
              )} - ${commonDate.fullDateStrToShortMonthDayString(
                containerDueDate,
              )}`;

              return (
                <Box key={thread.threadId} width="full">
                  <CommentThread
                    thread={thread}
                    organizationId={organizationId}
                    userId={userId}
                    refetchData={refetchThreads}
                  >
                    <>
                      <HStack
                        fontSize="sm"
                        mb={3}
                        mt={externalOrganizations.length > 0 ? 3 : 0}
                      >
                        {(type === 'lender' && receiptId) ||
                        type === 'borrower' ? (
                          <Link
                            fontWeight="bold"
                            color="blue.400"
                            href={`${
                              type === 'lender' && receiptId
                                ? getDeliverableFinalOutputReceiptsViewUrl({
                                    containerId,
                                    receiptId,
                                  })
                                : getDeliverableDetailViewUrl(containerId)
                            }?comments=true`}
                          >
                            {name}
                          </Link>
                        ) : (
                          <Text fontWeight="bold">{name}</Text>
                        )}
                        <Spacer />
                        <Text color={creditFacilityTextColor}>
                          {commonUtils.toTitleCase(creditFacilitySlug)}
                        </Text>
                      </HStack>
                      <Divider />
                    </>
                  </CommentThread>
                </Box>
              );
            })}
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
